import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AppLayout from '../components/AppLayout';
import NewPropertyModal from '../components/NewPropertyModal';
import { toast } from 'sonner';
import {
  Plus, Home as HomeIcon, Image as ImageIcon, Loader2,
  Search, Video, Zap, X,
} from 'lucide-react';

/**
 * Properties list page. The primary "what do I post about?" surface.
 *
 * Adds search + sort + status-filter on top of the previous bare grid
 * so a Makler with 30+ listings can scan to "the one with the new
 * price" or "everything currently auto-posting" without scrolling.
 *
 * Per-card badges:
 *   - Video count (corner): "🎬 N" if `video_count > 0`, hidden otherwise.
 *     Backend computes this in the /api/properties response.
 *   - Auto-Post pill: shown when `is_in_rotation` is true (recent queue
 *     activity for this property AND auto_post_configs.is_enabled).
 *
 * "Neue Immobilie" opens a modal (NewPropertyModal) instead of
 * routing to a full-page form. Less friction for what's a small task.
 */

const SORT_OPTIONS = [
  { value: 'updated_desc', labelKey: 'properties.sortUpdatedDesc' },
  { value: 'created_desc', labelKey: 'properties.sortCreatedDesc' },
  { value: 'title_asc',    labelKey: 'properties.sortTitleAsc' },
  { value: 'price_desc',   labelKey: 'properties.sortPriceDesc' },
  { value: 'price_asc',    labelKey: 'properties.sortPriceAsc' },
];

const FILTER_CHIPS = [
  { value: 'all',         labelKey: 'properties.filterAll' },
  { value: 'with_video',  labelKey: 'properties.filterWithVideo' },
  { value: 'in_rotation', labelKey: 'properties.filterInRotation' },
  { value: 'no_activity', labelKey: 'properties.filterNoActivity' },
];

// Best-effort extraction of a numeric Euro value from the property's
// price string (which can be "€ 450.000", "EUR 450,000", "450000", etc.).
// We just need a comparable number for sorting. Exact format doesn't
// matter as long as ordering is monotonic on the displayed value.
function priceToNumber(p) {
  if (p == null) return 0;
  const cleaned = String(p).replace(/[^\d]/g, '');
  return cleaned ? parseInt(cleaned, 10) : 0;
}

export default function PropertiesPage() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [filter, setFilter] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProperties = async () => {
    try {
      const response = await propertiesAPI.getAll();
      setProperties(response.data);
    } catch (error) {
      toast.error(t('properties.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Apply search → filter → sort in one pass. Memoized so typing
  // doesn't trigger extra work on un-related re-renders.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = properties;
    if (q) {
      rows = rows.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }
    if (filter === 'with_video') {
      rows = rows.filter(p => (p.video_count || 0) > 0);
    } else if (filter === 'in_rotation') {
      rows = rows.filter(p => p.is_in_rotation);
    } else if (filter === 'no_activity') {
      rows = rows.filter(p => !p.is_in_rotation && (p.video_count || 0) === 0);
    }
    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case 'created_desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'title_asc':
          return (a.title || '').localeCompare(b.title || '', 'de');
        case 'price_desc':
          return priceToNumber(b.price) - priceToNumber(a.price);
        case 'price_asc':
          return priceToNumber(a.price) - priceToNumber(b.price);
        case 'updated_desc':
        default:
          return new Date(b.updated_at || b.created_at || 0)
               - new Date(a.updated_at || a.created_at || 0);
      }
    });
    return rows;
  }, [properties, search, filter, sort]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // Empty state. First-time, no properties at all.
  if (properties.length === 0) {
    return (
      <AppLayout>
        <div>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-[#23211a]">{t('properties.title')}</h1>
              <p className="text-[#6f6a5c] mt-1">{t('properties.subtitle')}</p>
            </div>
          </div>
          <Card className="glass border-[#e3dccb] p-12">
            <div className="text-center">
              <HomeIcon className="w-16 h-16 text-[#b5ad99] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#23211a] mb-2">{t('properties.noProperties')}</h2>
              <p className="text-[#6f6a5c] mb-6">{t('properties.createFirst')}</p>
              <Button
                onClick={() => setShowNewModal(true)}
                className="bg-[#1f4d3a] hover:bg-[#163a2c]"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('properties.addFirst')}
              </Button>
            </div>
          </Card>
          {showNewModal && (
            <NewPropertyModal
              onClose={() => setShowNewModal(false)}
              onCreated={() => { setShowNewModal(false); loadProperties(); }}
            />
          )}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-[#23211a]">{t('properties.title')}</h1>
            <p className="text-[#6f6a5c] mt-1">
              {t('properties.countLine', { count: properties.length })
                || `${properties.length} Immobilien`}
            </p>
          </div>
          <Button
            data-testid="add-property-button"
            onClick={() => setShowNewModal(true)}
            className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('properties.addProperty')}
          </Button>
        </div>

        {/* Search + sort row. Mobile stacks vertically. */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#8a8474] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('properties.searchPlaceholder') || 'Titel oder Adresse suchen…'}
              className="pl-10 bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
              data-testid="properties-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40 sm:min-w-[200px]"
            data-testid="properties-sort"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey) || opt.value}
              </option>
            ))}
          </select>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTER_CHIPS.map(chip => {
            const active = filter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setFilter(chip.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  active
                    ? 'bg-[#dfe8db] text-[#2e6b50] border-[#1f4d3a]/50'
                    : 'bg-[#f2ecdf] text-[#6f6a5c] border-[#e3dccb] hover:border-[#1f4d3a]/40'
                }`}
                data-testid={`properties-filter-${chip.value}`}
              >
                {t(chip.labelKey) || chip.value}
              </button>
            );
          })}
        </div>

        {/* Grid OR no-match state */}
        {visible.length === 0 ? (
          <Card className="glass border-[#e3dccb] p-10 text-center">
            <Search className="w-10 h-10 text-[#b5ad99] mx-auto mb-3" />
            <p className="text-[#4a463b] mb-3">
              {t('properties.noMatches', { query: search })
                || `Keine Treffer${search ? ` für „${search}"` : ''}`}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSearch(''); setFilter('all'); }}
              className="border-[#d8d0bd] text-[#4a463b]"
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              {t('properties.resetFilters') || 'Filter zurücksetzen'}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((property) => (
              <Card
                key={property.id}
                data-testid={`property-card-${property.id}`}
                onClick={() => navigate(`/properties/${property.id}`)}
                className="glass border-[#e3dccb] hover:border-[#1f4d3a]/50 cursor-pointer transition-all duration-300 hover:-translate-y-1 group overflow-hidden relative"
              >
                {/* Status badges. Top-right corner. Only render when
                    truthy so cards stay quiet by default. */}
                <div className="absolute top-3 right-3 flex flex-col items-end gap-1 z-10">
                  {property.video_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-[#f7f3ea]/90 backdrop-blur-sm text-[#2e6b50] border border-[#1f4d3a]/40">
                      <Video className="w-3 h-3" />
                      {property.video_count}
                    </span>
                  )}
                  {property.is_in_rotation && !property.excluded_from_autopost && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-[#d6e8dd] backdrop-blur-sm text-[#1e7a4f] border border-[#1e7a4f]/40">
                      <Zap className="w-3 h-3" />
                      {t('properties.autoPostActive') || 'Auto-Post aktiv'}
                    </span>
                  )}
                  {property.excluded_from_autopost && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-[#f7f3ea]/90 backdrop-blur-sm text-[#6f6a5c] border border-[#c9c1ac]">
                      <Zap className="w-3 h-3 opacity-50" />
                      {t('properties.autoPostPaused') || 'Auto-Post pausiert'}
                    </span>
                  )}
                </div>
                {property.images && property.images[0] ? (
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-[#e9e2d2] flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-[#9c9585]" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-xl font-semibold text-[#23211a] mb-2 line-clamp-1">
                    {property.title}
                  </h3>
                  <p className="text-[#1f4d3a] font-medium text-lg mb-2">{property.price}</p>
                  <p className="text-sm text-[#6f6a5c] mb-3">{property.location}</p>
                  <div className="flex flex-wrap gap-2">
                    {(property.highlights || []).slice(0, 2).map((highlight, index) => (
                      <span
                        key={index}
                        className="text-xs px-2 py-1 bg-[#efe9db] text-[#4a463b] rounded"
                      >
                        {highlight}
                      </span>
                    ))}
                    {(property.highlights || []).length > 2 && (
                      <span className="text-xs px-2 py-1 bg-[#efe9db] text-[#4a463b] rounded">
                        {t('properties.more', { count: property.highlights.length - 2 })}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {showNewModal && (
          <NewPropertyModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => { setShowNewModal(false); loadProperties(); }}
          />
        )}
      </div>
    </AppLayout>
  );
}
